import { Injectable, Injector, Signal, computed, inject, signal } from '@angular/core';
import { Observable, distinctUntilChanged, filter, map, take } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslationModule } from './i18n.config';

const BASE_MODULE = '__base__';

/**
 * Відстежує готовність перекладів по кожному модулю окремо.
 *
 * Це дозволяє компоненту чекати саме на свій неймспейс, а не блокувати рендер
 * на всій системі перекладів. Стан живе у сигналах, тож шаблон може читати
 * `ready('olx')()` напряму, без підписок.
 */
@Injectable({ providedIn: 'root' })
export class TranslationReadyService {
  private readonly injector = inject(Injector);

  /** `<lang>|<module>` -> чи готовий модуль для цієї мови. */
  private readonly state = signal<Record<string, boolean>>({});

  /** Активна мова, для якої питають готовність. */
  private readonly language = signal<string>('');

  private readonly readyStreams = new Map<string, Observable<boolean>>();

  public setLanguage(language: string): void {
    this.language.set(language);
  }

  public markReady(module: TranslationModule | null, language: string): void {
    const key = this.key(module, language);
    this.state.update((current) => (current[key] ? current : { ...current, [key]: true }));
  }

  /**
   * При зміні мови модулі, завантажені для попередньої мови, вважаються
   * неготовими доти, доки не приїде відповідний файл нової мови.
   */
  public resetForLanguage(language: string): void {
    this.state.update((current) => {
      const next: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(current)) {
        if (!key.startsWith(`${language}|`)) {
          next[key] = value;
        }
      }
      return next;
    });
  }

  /** Сигнал готовності модуля для поточної мови. */
  public ready(module: TranslationModule | null): Signal<boolean> {
    return computed(() => Boolean(this.state()[this.key(module, this.language())]));
  }

  public isReady(module: TranslationModule | null, language: string): boolean {
    return Boolean(this.state()[this.key(module, language)]);
  }

  /** Емітить один раз, коли модуль готовий для поточної мови. */
  public whenReady(module: TranslationModule | null): Observable<boolean> {
    const streamKey = module ?? BASE_MODULE;
    let stream = this.readyStreams.get(streamKey);
    if (!stream) {
      stream = toObservable(this.ready(module), { injector: this.injector }).pipe(distinctUntilChanged());
      this.readyStreams.set(streamKey, stream);
    }
    return stream.pipe(
      filter(Boolean),
      take(1),
      map(() => true),
    );
  }

  private key(module: TranslationModule | null, language: string): string {
    return `${language}|${module ?? BASE_MODULE}`;
  }
}
